import { Injectable, OnDestroy } from "@angular/core";
import { WebShopService } from "../../services/webShop.service";
import Utils from "../../shared/utils";
import { CoreLogService } from "../../services/log.service";
import { BehaviorSubject, Observable } from "rxjs";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { IRemoteOrderGroupPaymentService, IMemberOrder, IMemberItem, IImpressumData, IDeliveryFeeInfo } from "./remoteOrderGroupPayment.facade";
import { GroupBasketItemUiData, GroupMemberUiData, GroupUiData, IGroupManageData, ILocationData } from "../../services/IRemoteGroupOrderService";
import { RemoteGroupOrderService } from "../../services/RemoteGroupOrder.service";
import { LocationDataModel, ScheduledForRequest } from "../../shared/models";
import { IObjectObserver } from "../../services/IObjectObserver";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { ContentType } from "../../shared/contentTypes";
import { ProductConfiguration, ProductConfigurationExtra } from "../../services/IWebShopService";

class MemberOrder implements IMemberOrder {
  private _dataModel: GroupMemberUiData | null;
  public items: MemberItem[] = [];
  public basketItems_amount: number;

  constructor(memberDataModel: GroupMemberUiData | null) {
    this._dataModel = memberDataModel;
  }

  public get model(): GroupMemberUiData {
    return this._dataModel;
  }

  public get userId(): number {
    return this._dataModel && this._dataModel.userId ? this._dataModel.userId : 0;
  }

  public get userName(): string {
    return this._dataModel && this._dataModel.name ? this._dataModel.name : "";
  }

  public get totalAmount(): number {
    return this.items.reduce((total, item) => total + item.price, 0);
  }
}

class MemberItem implements IMemberItem {
  private itemDataModel: GroupBasketItemUiData;

  constructor(itemDataModel: GroupBasketItemUiData) {
    this.itemDataModel = itemDataModel;
  }

  public get model(): GroupBasketItemUiData {
    return this.itemDataModel;
  }

  public get userId(): number {
    return this.itemDataModel.userId;
  }

  public get label(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(this.itemDataModel.name_en)
      ? this.itemDataModel.name_de
      : this.itemDataModel.name_en;
  }

  public get count(): number {
    return this.itemDataModel.count;
  }

  //!price with deposit and extra
  public get price(): number {
    return Utils.centsToCurrency(this.itemDataModel.price * this.itemDataModel.count);
  }
}

class ImpressumData implements IImpressumData {
  private _locationData: LocationDataModel;
  public get label(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(this._locationData.label_en) ? this._locationData.label_de : this._locationData.label_en;
  }
  public get address(): string {
    const zipAndLocation = [this._locationData.zip, this._locationData.location].filter(s => s).join(" ");
    return [this._locationData.street, zipAndLocation].filter(s => s).join(", ");
  }

  public get impressumHtml(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(this._locationData.impressum_html_en) ? this._locationData.impressum_html_de : this._locationData.impressum_html_en;
  }

  public get phone_no(): string {
    return this._locationData.phone_no;
  }

  public get stnr(): string {
    return this._locationData.stnr;
  }

  public get ustid(): string {
    return this._locationData.ustid;
  }

  public get commercial_register(): string {
    return this._locationData.commercial_register;
  }

  public get local_court(): string {
    return this._locationData.local_court;
  }

  public get representative(): string {
    return this._locationData.representative;
  }

  constructor(loc: LocationDataModel) {
    this._locationData = loc;
  }

}

@Injectable()
export class RemoteOrderGroupPaymentService implements IRemoteOrderGroupPaymentService, OnDestroy {

  /** The group management data */
  public get groupManageData(): IGroupManageData {
    return this._remoteGroupOrderService.groupManageData;
  }

  public get locationData$(): Observable<ILocationData> {
    return this._remoteGroupOrderService.locationData$;
  }

  private _orders: MemberOrder[] = [];
  /** The list of orders */
  private _ordersSubject: BehaviorSubject<ReadonlyArray<IMemberOrder>> = new BehaviorSubject<ReadonlyArray<IMemberOrder>>([]);
  public get orders$(): Observable<ReadonlyArray<IMemberOrder>> {
    return this._ordersSubject.asObservable();
  }

  /** Delivery fee info */
  private _deliveryFeeInfoSubject: BehaviorSubject<IDeliveryFeeInfo> = new BehaviorSubject<IDeliveryFeeInfo>(null);
  public get deliveryFeeInfo$(): Observable<IDeliveryFeeInfo> {
    return this._deliveryFeeInfoSubject.asObservable();
  }

  /** Whether tipping is allowed */
  private _canTipSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public get canTip$(): Observable<boolean> {
    return this._canTipSubject.asObservable();
  }

  //TODO 
  private _hasScheduledItems: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** Observable for whether there are scheduled items in the basket */
  public get hasScheduledItems$(): Observable<boolean> {
    return this._hasScheduledItems.asObservable();
  }

  private _tipAsPercent: boolean = true;
  private _tippingPercent: number = 0;
  /** Tipping percentage */
  private _tippingPercentSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get tippingPercent$(): Observable<number> {
    return this._tippingPercentSubject.asObservable();
  }

  private _tippingAmount: number = 0;
  /** Tipping amount */
  private _tippingAmountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get tippingAmount$(): Observable<number> {
    return this._tippingAmountSubject.asObservable();
  }

  private _total: number = 0;
  /** Basket total amount(icludes tip / delivery) */
  private _totalSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get total$(): Observable<number> {
    return this._totalSubject.asObservable();
  }

  private _sum: number = 0;
  /** Basket sum (no tip or delivery fee*/
  private _sumSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get sum$(): Observable<number> {
    return this._sumSubject.asObservable();
  }

  private _showTipping: boolean;
  /** Returns whether tipping is shown */
  public get showTipping(): boolean {
    return this._showTipping;
  }

  private _canContinue: boolean = false;
  /** Indicates if the button to proceed to checkout is active */
  private _isCheckoutButtonActiveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public get canContinue$(): Observable<boolean> {
    return this._isCheckoutButtonActiveSubject.asObservable();
  }

  /** Returns whether the user has accepted the terms and conditions (AGB) */
  public get hasAcceptedAGB(): boolean {
    return this._webShopService.hasAcceptedAGB;
  }
  /** Sets whether the user has accepted the terms and conditions (AGB) */
  public set hasAcceptedAGB(accepted: boolean) {
    this._webShopService.hasAcceptedAGB = accepted;
  }

  private _locationObsServ: IObjectObserver<LocationDataModel>;
  /** Location impressum data */
  private _locationImpressumData: ImpressumData;
  public get locationImpressumData(): IImpressumData {
    return this._locationImpressumData;
  }

  public get hasCustomerInputFields() {
    const me = this;
    return me._webShopService.hasCustomerInputFields;
  }

  constructor(
    private _remoteGroupOrderService: RemoteGroupOrderService,
    private _webShopService: WebShopService,
    private _coreLogService: CoreLogService,
    public _objObsInjectHelper: CircularInjectionHelper,
  ) {
    const me = this;
    me._locationObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<LocationDataModel>(ContentType.Location, _objObsInjectHelper);
    me._showTipping = me._webShopService.ordertype ? me._webShopService.isTippingEnabled() : false;
    if (me._showTipping) {
      me._canTipSubject.next(me._showTipping);
      me.setTippingPercent(me._webShopService.ordertype ? me._webShopService.ordertype.tip_percentage : 0);
    }
    me.loadLocationImpressumData();
    me.getLocationData().then();
    me._remoteGroupOrderService.getGroupData().then(async (groupData) => {
      await me.loadGroupOrder(groupData);
      me.updateDeliveryFeeInfo();
    });
  }

  ngOnDestroy(): void { }

  public updateDeliveryFeeInfo() {
    const me = this;
    const finalDeliveryFee = me._webShopService.finalDeliveryFee;
    const showFreeShippingNotice = me._webShopService.useFreeShipping && me._webShopService.remainingAmountUntilFreeShipping > 0 && finalDeliveryFee > 0;

    // Für Remote Group Orders auf Basis der Gruppensumme prüfen
    const groupTotal = me._sum || 0;
    const minimumOrderValue = me._webShopService.minimumOrderValue || 0;
    // Nur prüfen wenn minimumOrderValue > 0 gesetzt ist
    const meetMinimumPriceForGroup = minimumOrderValue <= 0 || groupTotal >= minimumOrderValue;

    const deliveryFeeInfo: IDeliveryFeeInfo = {
      deliveryFee: me._webShopService.deliveryFee,
      finalDeliveryFee: finalDeliveryFee,
      minimumDeliveryValue: minimumOrderValue,
      remainingAmount: me._webShopService.remainingAmountUntilFreeShipping,
      minimumPriceRequirementIsMet: meetMinimumPriceForGroup,
      showFreeShippingNotice: showFreeShippingNotice
    };

    me._deliveryFeeInfoSubject.next(deliveryFeeInfo);
  }

  private async loadGroupOrder(groupData: GroupUiData): Promise<void> {
    const me = this;
    const memberOrders: MemberOrder[] = groupData.members.map((member) => {
      const items = groupData.basket_items
        .filter((item) => item.userId === member.userId)
        .map((item) => new MemberItem(item));

      const memberOrder = new MemberOrder(member);
      memberOrder.items = items;

      return memberOrder;
    });
    me._orders = memberOrders;

    me._webShopService.clearItems();
    me._webShopService.setServiceOrderTime(me.groupManageData.selectedServiceTime);
    const locationID = me._webShopService.locationID;
    for (const memberOrder of memberOrders) {
      for (const item of memberOrder.items) {
        const extras: ProductConfigurationExtra[] = [];
        for (const extra of item.model.extras) {
          extras.push({ id: extra.id, count: extra.count });
        }
        const config: ProductConfiguration = {
          extras: extras,
          count: item.count,
          cus_ref_id: memberOrder.userId,
          comment: item.model.comment
        };
        const success = await me._webShopService.addItemToShippingCartAndCheckAge(locationID, item.model.id, config);
        if (!success) {
          me._coreLogService.error("RemoteOrderGroupPaymentService.loadGroupOrder", "addItemToShippingCartAndCheckAge fail", locationID, item.model.productId, config);
        }
      }
    }
    const sum = memberOrders.reduce((total, order) => total + order.totalAmount, 0);
    me._setSum(sum)
    me._ordersSubject.next(me._orders);
  }

  /** Sets tipping percentage and updates price. */
  public setTippingPercent(percent: number) {
    const me = this;
    if (me._tipAsPercent != true || me._tippingPercent != percent) {
      me._tippingPercent = percent;
      me._tippingPercentSubject.next(me._tippingPercent);
      me._tipAsPercent = true;
      me.recountPrice();
    }
  }

  /** Sets tipping amount and updates price. */
  public setTippingAmount(amount: number) {
    const me = this;
    if (me._tipAsPercent != false || me._tippingAmount != amount) {
      me._tippingAmount = amount;
      me._tipAsPercent = false;
      me.recountPrice();
    }
  }

  /** Loads the impressum data for the current location.*/
  private async loadLocationImpressumData() {
    const me = this;
    const currentLocation = me._remoteGroupOrderService.currentLocationID; // me._webShopService.locationID;
    const locationData = await me._locationObsServ.GetObject(currentLocation);
    this._locationImpressumData = new ImpressumData(locationData);
  }

  private async getLocationData() {
    const me = this;
    await me._remoteGroupOrderService.getCurrentLocationData();
  }

  /** Set sum and update price */
  private _setSum(sum: number): void {
    const me = this;
    if (me._sum != sum) {
      me._sum = sum;
      me._sumSubject.next(me._sum);
      me.recountPrice();
    }
  }

  /**
  * Updates total price with tip.
  * Calculates tip if percentage-based.
  * Updates observables and checkout button state.
  */
  private recountPrice(): void {
    const me = this;
    if (me._tipAsPercent) {
      me._tippingAmount = +((me._sum / 100 * me._tippingPercent).toFixed(1));
      me._tippingAmountSubject.next(me._tippingAmount);
    } // else keep and use tippingAmount
    me._total = me._sum + me._tippingAmount;
    me._totalSubject.next(me._total);
    me._setCanContinue(me._total > 0);
  }

  /**
 * Sets whether the checkout button can be active.
 * Updates the observable for the checkout button state.
 */
  private _setCanContinue(canContinue: boolean): void {
    const me = this;
    if (me._canContinue != canContinue) {
      me._canContinue = canContinue;
      me._isCheckoutButtonActiveSubject.next(me._canContinue);
    }
  }

  public timeDisplayValue(time: string) {
    const me = this;
    return me._remoteGroupOrderService.timeDisplayValue(time);
  }

  public dayDisplayValue(day: string) {
    const me = this;
    return me._remoteGroupOrderService.dayDisplayValue(day);
  }

  public isDayToday(selectedDay: string) {
    const me = this;
    return me._remoteGroupOrderService.isDayToday(selectedDay)
  }

  // ! Currently, there is no check for scheduled items
  //TODO schedule items 
  //TODO remove schedule items
  public removeScheduledItems(item: any) {
    // return this._webShopService.removeScheduledItems(item);
  }
  //TODO check if there are scheduled items
  /** List of scheduled items that are outside the order time */
  public async getScheduledItems(schedule_req: ScheduledForRequest): Promise<any[]> {
    const me = this;
    let scheduledItems = [];
    return new Promise<any[]>((resolve, reject) => {
      try {

      } catch (error) {
        me._coreLogService.error("RemoteOrderGroupPaymentService.getScheduledItems", "try catch", error);
      }
    })
  }
  //TODO scheduled items
  /** Remove all scheduled items that are outside the order time */
  public removeScheduledItemsFromBasket(basketItems: any[]) {
    const me = this;
    basketItems.forEach((basketItem) => {
      basketItem.removeScheduledItems();
      me._hasScheduledItems.next(false)
    })
  }
}